<template>
  <div class="profile-parent-rel">
    <div class="profile-family-card">
      <div class="father">
        <ParentConnector layout="small"/>
        <ProfileRelationshipCard :rel-person-id="fatherIdRef" :context-person-id="childId" :is-rel-owner="isRelOwnerRef" :owner-only="props.ownerOnly"/>
      </div>
      <div class="mother">
        <ParentConnector layout="small" mother/>
        <ProfileRelationshipCard :rel-person-id="motherIdRef" :context-person-id="childId" :is-rel-owner="isRelOwnerRef" :owner-only="props.ownerOnly"/>
      </div>
    </div>
    <div v-if="canEditRef" class="action-row">
      <button type="button" class="btn btn-inline btn-link" @click="onChange">
        Change
      </button>
      <button type="button" class="btn btn-inline btn-link" @click="onRemove">
        Remove
      </button>
    </div>
    <div class="owners-row">
      <button type="button" v-if="!props.ownerOnly && hasOtherOwnersRef" class="btn btn-inline btn-link link-secondary" @click="showOwnersModal">
        Relationship defined in <span class="count"> ({{ relOwnerProfileIdsRef.length }})</span>
      </button>
    </div>
    <ProfileChildren
      :personId="childId"
      :family="parentFamily"
      @add-child="emit('addSibling')">
    </ProfileChildren>
    <ProfileParentRelationshipModal ref="parentRelModalRef" @change="onChangeParent" @remove="onConfirmRemoveParent"/>
    <ProfileSpouseRelationshipModal ref="spouseRelModalRef" as-child/>
    <ProfileRelationshipOwnersModal ref="ownersModalRef"
      :person-id="childId"
      :owner-profile-ids="relOwnerProfileIdsRef"/>
    <ProfileParentRemoveModal ref="removeParentModalRef" @ok="onRemoveParent"/>
  </div>
</template>

<style lang="scss" scoped>
.profile-parent-rel {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.profile-family-card {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  & > .father, & > .mother {
    display: flex;
    max-width: 500px;
  }
}

.alert {
  margin: 0.25rem 1rem;
}

.expander {
  margin-left: 1rem;
  font-size: 0.875rem;
}

.expander-label {
  color: #888;
}

.count {
  font-size: 0.75rem;
  color: #aaa;
}

.action-row {
  margin-left: 1rem;
  display: flex;
  gap: 1rem;
  font-size: 0.875rem;
}

.owners-row {
  margin-left: 1rem;
  font-size: 0.75rem;
}

.link-secondary {
  color: #888;

  &:hover {
    color: #333;
  }
}
</style>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { FamilyAndChildren } from '@/rd/ViewFamilyStore'
import { AssertionType, FamilyRelationshipRole } from '@/rd/ResearchDataModel'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import { useViewFamilyStore } from '@/rd/ViewFamilyStore'
import { useAssertionStore } from '@/rd/AssertionStore'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { CompositeId } from '@/rd/CompositeId'
import { ItemPermissions } from '@/gp/GroupAdminModel'
import ParentConnector from '@/manage/ParentConnector.vue'
import ProfileRelationshipCard from './ProfileRelationshipCard.vue'
import ProfileChildren from './ProfileChildren.vue'
import ProfileRelationshipOwnersModal from './ProfileRelationshipOwnersModal.vue'
import ProfileParentRelationshipModal from './ProfileParentRelationshipModal.vue'
import ProfileSpouseRelationshipModal from './ProfileSpouseRelationshipModal.vue'
import Modal from '@/util/Modal.vue'
import ProfileParentRemoveModal from './ProfileParentRemoveModal.vue'
import { setProfileSpouseAsync } from './ProfileManager'

const props = defineProps<{
  childId: string,
  parentFamily: FamilyAndChildren,
  ownerOnly?: boolean,
}>()

const emit = defineEmits([
  'addSibling',
  'remove',
])

const parentRelModalRef = ref<InstanceType<typeof ProfileParentRelationshipModal>>()
const spouseRelModalRef = ref<InstanceType<typeof ProfileSpouseRelationshipModal>>()
const ownersModalRef = ref<InstanceType<typeof ProfileRelationshipOwnersModal>>()
const removeParentModalRef = ref<InstanceType<typeof ProfileParentRemoveModal>>()

const viewPersonStore = useViewPersonStore()
const viewFamilyStore = useViewFamilyStore()
const assertionStore = useAssertionStore()
const dataGroupStore = useDataGroupStore()

const childGroupId = computed(() => CompositeId.getGroupId(props.childId))
const viewFamilyRef = computed(() => viewFamilyStore.getAsyncFamily(props.parentFamily.family.id)?.data)
const familyIdRef = computed(() => viewFamilyRef.value?.matchIds.find(id => CompositeId.hasGroupId(id, childGroupId.value!)))
const fatherIdRef = computed(() => viewFamilyRef.value?.fatherId)
const motherIdRef = computed(() => viewFamilyRef.value?.motherId)

const childViewPersonRef = computed(() => viewPersonStore.getLoadedPerson(props.childId))
const relOwnerProfileIdsRef = computed(() => {
  // child relationship owners are the child matches that have a child assertion whose related family is
  // one of the family matches for this relationship
  return childViewPersonRef.value?.matchIds.flatMap(id => assertionStore.getLoadedForPerson(id))
    .filter(a => a.assertionType == AssertionType.Child && props.parentFamily.family.matchIds.includes(a.relatedItemId!))
    .map(a => a.subjectId!) ?? []
})
const isRelOwnerRef = computed(() => relOwnerProfileIdsRef.value.includes(props.childId))
const hasOtherOwnersRef = computed(() => relOwnerProfileIdsRef.value.some(id => id !== props.childId))
const childPermissionsRef = computed(() => dataGroupStore.getLoadedPermissions(CompositeId.getGroupId(props.childId)))
const canEditChildRef = computed(() => (childPermissionsRef.value & ItemPermissions.Modify) > 0)
const canEditRef = computed(() => canEditChildRef.value && relOwnerProfileIdsRef.value.includes(props.childId))

function showOwnersModal() {
  ownersModalRef.value?.open()
}

function onChange() {
  parentRelModalRef.value?.open(props.childId, props.parentFamily.family)
}

function onChangeParent(role: FamilyRelationshipRole) {
  const childGroupId = CompositeId.getGroupId(props.childId)!
  spouseRelModalRef.value?.open(childGroupId, props.parentFamily.family, role)
}

function onConfirmRemoveParent(role: FamilyRelationshipRole) {
  removeParentModalRef.value?.open(role, props.parentFamily.family.getSpouseId(role)!)
}

async function onRemoveParent(role: FamilyRelationshipRole) {
  if (familyIdRef.value) {
    await setProfileSpouseAsync(familyIdRef.value, undefined, role)
  }
}

function onRemove() {
  emit('remove', props.parentFamily.family)
}
</script>
